import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { startSimulation, sendMessage, endSimulation, textToSpeech, speechToText } from '../lib/api';
import { Phone, PhoneOff, Volume2, User, Mic } from 'lucide-react';
import { playCallStartSound, playGameOverSound, playTimerWarningSound } from '../lib/sounds';

export default function SimulationPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const profileId = searchParams.get('profile');

  const [session, setSession] = useState(null);
  const [timer, setTimer] = useState(90);
  const [isActive, setIsActive] = useState(false);
  const [loading, setLoading] = useState(false);
  const [callEnded, setCallEnded] = useState(false);

  const [isAiSpeaking, setIsAiSpeaking] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [micReady, setMicReady] = useState(false);
  const [volume, setVolume] = useState(0);

  const timerRef = useRef(null);
  const audioRef = useRef(null);
  const callEndedRef = useRef(false);
  const sessionRef = useRef(null);

  // VAD refs
  const streamRef = useRef(null);
  const audioCtxRef = useRef(null);
  const analyserRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const rafRef = useRef(null);
  const isSpeakingRef = useRef(false);
  const silenceStartRef = useRef(null);
  const isProcessingRef = useRef(false);
  const isAiSpeakingRef = useRef(false);

  // Keep refs in sync
  useEffect(() => { isProcessingRef.current = isProcessing; }, [isProcessing]);
  useEffect(() => { isAiSpeakingRef.current = isAiSpeaking; }, [isAiSpeaking]);

  // Timer - only when user speaks or processing
  const timerActiveRef = useRef(false);
  useEffect(() => {
    // Timer ONLY when user is actively speaking — NOT during processing or AI speaking
    timerActiveRef.current = isSpeaking;
  }, [isSpeaking]);

  const endingTriggeredRef = useRef(false);

  useEffect(() => {
    if (isActive && timer > 0) {
      timerRef.current = setInterval(() => {
        if (!timerActiveRef.current) return;
        setTimer(prev => {
          if (prev <= 1) {
            clearInterval(timerRef.current);
            triggerEndCall();
            return 0;
          }
          if (prev === 11 || prev === 6) {
            try { playTimerWarningSound(); } catch {}
          }
          // At 10 seconds: trigger AI ending
          if (prev === 10 && !endingTriggeredRef.current && sessionRef.current) {
            endingTriggeredRef.current = true;
            sendMessage(sessionRef.current.session_id, '[SYSTEME: Il reste 10 secondes. Tu dois conclure l\'appel maintenant. Choisis UNE de ces conclusions selon comment s\'est passé l\'appel : 1) Si l\'appelant était convaincant : "Écoutez, envoyez-moi un mail avec toutes les infos, je regarde ça." ou "OK, organisons une interview." 2) Si l\'appelant était moyen : "Envoyez-moi un mail avec un résumé, on verra." 3) Si l\'appelant était mauvais : "Ça ne m\'intéresse pas, merci au revoir." Réponds en 1 phrase maximum.]')
              .then(({ data }) => {
                if (!callEndedRef.current) playTts(data.response);
              }).catch(() => {});
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(timerRef.current);
    }
  }, [isActive]); // eslint-disable-line react-hooks/exhaustive-deps

  // Persistent Audio element — pre-unlocked on iOS by user tap
  const persistentAudioRef = useRef(null);
  const ttsQueueRef = useRef([]);
  const isPlayingTtsRef = useRef(false);

  const unlockAudio = useCallback(() => {
    // Create and play a silent audio to unlock iOS audio
    if (!persistentAudioRef.current) {
      const audio = new Audio();
      audio.src = 'data:audio/mp3;base64,SUQzBAAAAAAAI1RTU0UAAAAPAAADTGF2ZjU4Ljc2LjEwMAAAAAAAAAAAAAAA/+M4wAAAAAAAAAAAAEluZm8AAAAPAAAAAwAAAbAAqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqqq1dXV1dXV1dXV1dXV1dXV1dXV1dXV1dXV1dXV1dXV1dXV1dXV////////////////////////////////////////////AAAAAExhdmM1OC4xMwAAAAAAAAAAAAAAACQCkAAAAAAAAAGwjYVJiAAAAAAAAAAAAAAAAAAAAP/jOMAAAk9JKAgBEAI8iFh0XREQBGJ/E/hJf5R84PnBB94IOAgGPn/KHP/B94f/8QfUHz/l/+D4Pg+8H/gh/+D58H/4f/wfP/5QfB8//8=';
      audio.volume = 0.01;
      audio.play().then(() => {
        audio.pause();
        audio.currentTime = 0;
        audio.volume = 1.0;
      }).catch(() => {});
      persistentAudioRef.current = audio;
    }
  }, []);

  const playNextTts = useCallback(() => {
    if (isPlayingTtsRef.current || ttsQueueRef.current.length === 0) return;
    const base64 = ttsQueueRef.current.shift();
    isPlayingTtsRef.current = true;

    const audio = persistentAudioRef.current;
    if (audio) {
      audio.src = `data:audio/mp3;base64,${base64}`;
      audio.onended = () => {
        isPlayingTtsRef.current = false;
        setIsAiSpeaking(false);
        playNextTts(); // Play next if queued
      };
      audio.onerror = () => {
        isPlayingTtsRef.current = false;
        setIsAiSpeaking(false);
      };
      audio.play().catch(() => {
        isPlayingTtsRef.current = false;
        setIsAiSpeaking(false);
      });
    } else {
      // Fallback: new Audio
      const newAudio = new Audio(`data:audio/mp3;base64,${base64}`);
      audioRef.current = newAudio;
      newAudio.onended = () => { isPlayingTtsRef.current = false; setIsAiSpeaking(false); };
      newAudio.onerror = () => { isPlayingTtsRef.current = false; setIsAiSpeaking(false); };
      newAudio.play().catch(() => { isPlayingTtsRef.current = false; setIsAiSpeaking(false); });
    }
  }, []);

  // ===== TTS =====
  const playTts = useCallback((text) => {
    const voice = sessionRef.current?.voice || 'onyx';
    setIsAiSpeaking(true);
    textToSpeech(text, voice)
      .then(({ data }) => {
        if (callEndedRef.current) { setIsAiSpeaking(false); return; }
        ttsQueueRef.current.push(data.audio);
        playNextTts();
      })
      .catch(() => setIsAiSpeaking(false));
  }, [playNextTts]);

  // ===== PROCESS RECORDED AUDIO =====
  const processAudio = useCallback(async (blob) => {
    if (callEndedRef.current || !sessionRef.current || blob.size < 1000) return;

    setIsProcessing(true);
    const formData = new FormData();
    formData.append('file', blob, 'recording.webm');
    try {
      const { data: sttData } = await speechToText(formData);
      if (sttData.text?.trim() && !callEndedRef.current) {
        const { data: aiData } = await sendMessage(sessionRef.current.session_id, sttData.text.trim());
        if (!callEndedRef.current) {
          playTts(aiData.response);
        }
      }
    } catch (e) {
      console.error('Process error:', e);
    }
    setIsProcessing(false);
  }, [playTts]);

  // ===== START LISTENING =====
  const startListening = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
      audioCtxRef.current = audioCtx;
      const source = audioCtx.createMediaStreamSource(stream);
      const analyser = audioCtx.createAnalyser();
      analyser.fftSize = 256;
      analyser.smoothingTimeConstant = 0.5;
      source.connect(analyser);
      analyserRef.current = analyser;

      setMicReady(true);
      startNewRecording(stream);
      monitorVolume();
    } catch (e) {
      console.error('Mic access error:', e);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const startNewRecording = (stream) => {
    if (!stream?.active) return;
    const mimeType = MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : 'audio/mp4';
    try {
      const recorder = new MediaRecorder(stream, { mimeType });
      recorderRef.current = recorder;
      chunksRef.current = [];

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      recorder.onstop = () => {
        const mType = recorder.mimeType || mimeType;
        const blob = new Blob(chunksRef.current, { type: mType });
        chunksRef.current = [];

        // Process if significant audio
        if (blob.size > 1000) {
          processAudio(blob);
        }

        // Restart recording after a short delay
        setTimeout(() => {
          if (!callEndedRef.current && streamRef.current?.active) {
            startNewRecording(streamRef.current);
          }
        }, 300);
      };

      recorder.start(100); // Collect data every 100ms
    } catch (e) {
      console.error('Recorder error:', e);
    }
  };

  const monitorVolume = () => {
    const check = () => {
      if (!analyserRef.current || callEndedRef.current) return;

      const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);
      analyserRef.current.getByteFrequencyData(dataArray);
      const avg = dataArray.reduce((a, b) => a + b, 0) / dataArray.length / 255;
      setVolume(avg);

      const threshold = 0.03;
      const now = Date.now();

      if (avg > threshold) {
        // Speaking
        if (!isSpeakingRef.current && !isProcessingRef.current && !isAiSpeakingRef.current) {
          isSpeakingRef.current = true;
          setIsSpeaking(true);
        }
        silenceStartRef.current = null;
      } else if (isSpeakingRef.current) {
        // Silence after speech
        if (!silenceStartRef.current) {
          silenceStartRef.current = now;
        } else if (now - silenceStartRef.current > 1800) {
          // 1.8s silence = user stopped talking
          isSpeakingRef.current = false;
          setIsSpeaking(false);
          silenceStartRef.current = null;

          // Stop recorder to trigger send
          if (recorderRef.current?.state === 'recording' && !isProcessingRef.current && !isAiSpeakingRef.current) {
            recorderRef.current.stop();
          }
        }
      }

      rafRef.current = requestAnimationFrame(check);
    };
    check();
  };

  // ===== STOP LISTENING =====
  const stopListening = useCallback(() => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    if (recorderRef.current?.state === 'recording') {
      try { recorderRef.current.stop(); } catch {}
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
    if (audioCtxRef.current) {
      try { audioCtxRef.current.close(); } catch {}
      audioCtxRef.current = null;
    }
    analyserRef.current = null;
    setMicReady(false);
    setIsSpeaking(false);
    setVolume(0);
  }, []);

  // ===== START CALL =====
  const handleStartCall = async () => {
    if (!profileId) return;
    setLoading(true);
    // Unlock iOS audio on user tap
    unlockAudio();
    try {
      const { data } = await startSimulation(profileId);
      setSession(data);
      sessionRef.current = data;
      setIsActive(true);
      try { playCallStartSound(); } catch {}

      // AI picks up
      setIsAiSpeaking(true);
      const firstMsg = await sendMessage(data.session_id, '[APPEL ENTRANT - Le téléphone sonne, vous décrochez]');
      playTts(firstMsg.data.response);

      // Start mic
      startListening();
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  };

  // ===== END CALL =====
  const triggerEndCall = useCallback(() => {
    if (callEndedRef.current) return;
    callEndedRef.current = true;
    setCallEnded(true);
    setIsActive(false);
    clearInterval(timerRef.current);
    stopListening();
    if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
    if (persistentAudioRef.current) { persistentAudioRef.current.pause(); }
    ttsQueueRef.current = [];
    isPlayingTtsRef.current = false;
    try { playGameOverSound(); } catch {}

    const sid = sessionRef.current?.session_id;
    if (sid) {
      navigate(`/feedback/${sid}`, { state: { loading: true } });
      endSimulation(sid).catch(() => {});
    } else {
      navigate('/dashboard');
    }
  }, [navigate, stopListening]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopListening();
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [stopListening]);

  const timerRunning = isSpeaking;
  const timerColor = !timerRunning ? '#71717A' : timer > 30 ? '#39FF14' : timer > 10 ? '#FFF200' : '#E61C24';
  const timerPulse = (timerRunning && timer <= 10) ? 'animate-[pulse_0.5s_ease-in-out_infinite]' : '';
  const timerDisplay = `${Math.floor(timer / 60)}:${String(timer % 60).padStart(2, '0')}`;

  const statusColor = isSpeaking ? '#39FF14' : isAiSpeaking ? '#00AEEF' : isProcessing ? '#FFF200' : '#71717A';
  const statusText = isSpeaking ? 'VOUS PARLEZ' : isAiSpeaking ? 'PARLE...' : isProcessing ? 'TRAITEMENT...' : 'A VOUS';
  const barColor = isSpeaking ? '#39FF14' : isAiSpeaking ? '#00AEEF' : '#333';

  // ===== PRE-CALL =====
  if (!session) {
    return (
      <div className="fixed inset-0 bg-[#050505] flex flex-col items-center justify-center p-6" style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
        <div className="w-full max-w-sm text-center">
          <div className="bg-black border-4 border-gray-800 shadow-[inset_0_0_30px_rgba(0,0,0,1)] p-6 mb-6">
            <div className="font-mono text-6xl text-[#FF333B] drop-shadow-[0_0_15px_rgba(255,51,59,0.8)]">1:30</div>
          </div>
          <p className="font-heading text-xl text-[#FFF200] uppercase tracking-widest mb-6">READY?</p>
          <button
            data-testid="start-call-btn"
            onClick={handleStartCall}
            disabled={loading}
            className="w-24 h-24 rounded-full bg-[#39FF14] border-4 border-green-800 shadow-[0_0_30px_rgba(57,255,20,0.6)] active:scale-90 transition-transform flex items-center justify-center mx-auto mb-3 disabled:opacity-50"
          >
            {loading ? <div className="w-8 h-8 border-4 border-black border-t-transparent rounded-full animate-spin" /> : <Phone className="text-black" size={36} />}
          </button>
          <p className="font-heading text-base text-[#39FF14] uppercase tracking-wider">{loading ? 'CONNEXION...' : 'APPELER'}</p>
          <button data-testid="back-dashboard-btn" onClick={() => navigate('/dashboard')} className="mt-6 font-mono text-sm text-[#71717A]">Retour</button>
        </div>
      </div>
    );
  }

  // ===== ACTIVE CALL — fixed viewport, safe area =====
  return (
    <div className="fixed inset-0 bg-[#050505] flex flex-col" style={{ paddingTop: 'env(safe-area-inset-top, 0px)', paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
      {/* Top section: timer + avatar + name */}
      <div className="text-center pt-4 pb-1 flex-shrink-0">
        <div className={`font-mono text-4xl ${timerPulse}`} style={{ color: timerColor, filter: `drop-shadow(0 0 10px ${timerColor})` }}>
          {timerDisplay}
        </div>
      </div>

      <div className="text-center pb-1 flex-shrink-0">
        <div className="w-16 h-16 rounded-full bg-[#1a1a1e] border-2 border-[#E61C24]/50 mx-auto mb-1 flex items-center justify-center">
          <User className="text-[#E61C24]" size={28} />
        </div>
        <h2 className="font-heading text-base uppercase tracking-wider text-white">{session.profile_name}</h2>
      </div>

      {/* Middle: status + visualizer — takes remaining space */}
      <div className="flex-1 flex flex-col items-center justify-center min-h-0">
        <p className="font-mono text-sm mb-3" style={{ color: statusColor }}>{statusText}</p>

        {/* Volume bars */}
        <div className="flex items-end justify-center gap-1 h-12">
          {Array.from({ length: 9 }, (_, i) => {
            const h = (isSpeaking || isAiSpeaking)
              ? Math.max(4, Math.min(48, volume * 200 + Math.sin(Date.now() / 150 + i * 0.7) * 12))
              : 4;
            return (
              <div
                key={i}
                className="w-1.5 rounded-full"
                style={{ height: `${h}px`, backgroundColor: barColor, boxShadow: h > 8 ? `0 0 4px ${barColor}` : 'none', transition: 'height 0.1s' }}
              />
            );
          })}
        </div>

        <div className="mt-3 flex items-center gap-1.5">
          <Mic size={12} className={micReady ? 'text-[#39FF14]' : 'text-[#71717A]'} />
          <span className="font-mono text-[10px] text-[#71717A]">{micReady ? 'MICRO ACTIF' : 'MICRO OFF'}</span>
        </div>
      </div>

      {/* Bottom: hang up */}
      <div className="pb-4 pt-2 flex-shrink-0 text-center">
        <button
          data-testid="end-call-btn"
          onClick={triggerEndCall}
          className="w-14 h-14 rounded-full bg-[#E61C24] shadow-[0_0_15px_rgba(230,28,36,0.5)] active:scale-90 transition-transform flex items-center justify-center mx-auto"
        >
          <PhoneOff className="text-white" size={24} />
        </button>
        <p className="font-mono text-[10px] text-[#71717A] mt-1">RACCROCHER</p>
      </div>
    </div>
  );
}
